/**
 * **The one clip player** — canon `docs/design/analiza-wideo-2026-09-23/`, boards FV4-06 to FV4-11.
 *
 * A full-viewport modal over the client's card, opened by `?clip=` from every door: the Forma tab's
 * dock, chart and table, and the attention queue's clip cards. Its own player over the native
 * `<video>`, no library. Escape closes it, except from a text field, where it hands the keyboard
 * back to the film.
 */

import { Dialog } from '@base-ui/react/dialog';
import { useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { ClipEntry } from '~/entities/exercise-clip';
import { isTextEntry } from '~/shared/lib/isTextEntry';
import { wrapTab } from '~/shared/lib/wrapTab';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import ghost from '~/shared/ui/ghostButton.module.css';
import kbd from '~/shared/ui/kbd.module.css';
import { ChevronGlyph, KeyboardGlyph } from '~/shared/ui/VideoGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { keyTargetOf } from '../lib/keyTarget';
import { useClipPlayback } from '../lib/useClipPlayback';
import { useClipPlayerData } from '../lib/useClipPlayerData';
import {
  neighbourIndex,
  nextUnreadIndex,
  panOrigin,
  pinJump,
  ZOOM_CENTRE,
  type ZoomOrigin,
} from '../model/playback';
import { playerCommand, type PlayerCommand } from '../model/playerKeys';

import styles from './ClipPlayer.module.css';
import { PlayerAside } from './PlayerAside';
import { PlayerControls, type PlayerTool } from './PlayerControls';
import { PlayerList } from './PlayerList';
import { PlayerStage } from './PlayerStage';
import { ShortcutsSheet } from './ShortcutsSheet';

const NO_TOOLS: Record<PlayerTool, boolean> = { compare: false, zoom: false, theatre: false };

export interface ClipPlayerProps {
  readonly clientId: number;
  readonly clipId: number;
  readonly onSelectClip: (clipId: number) => void;
  readonly onClose: () => void;
  /** The trainer saw or answered a clip; the host refreshes what it lists from that. */
  readonly onActivity: () => void;
  /** The second the film opens at, when the side panel's small player was left there. */
  readonly startAt?: number | null;
  /**
   * The word on the way back, naming where it leads: „Forma" over the client's card (the default),
   * „Czat" when the chat opened the film (chat canon, board C11).
   */
  readonly backLabel?: string;
}

export function ClipPlayer({
  clientId,
  clipId,
  onSelectClip,
  onClose,
  onActivity,
  startAt = null,
  backLabel,
}: ClipPlayerProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  // The seen mark's refusal is said in the status line, and goes when the trainer writes.
  const data = useClipPlayerData(clientId, clipId, onActivity, () => {
    setStatus(t('web:clipPlayer.states.seenFailed'));
  });
  const playback = useClipPlayback(
    () => {
      data.markSeen(clipId);
    },
    { autoplay: true, at: startAt },
  );
  const [tools, setTools] = useState<Record<PlayerTool, boolean>>(NO_TOOLS);
  const [origin, setOrigin] = useState<ZoomOrigin>(ZOOM_CENTRE);
  const [sheet, setSheet] = useState(false);
  const popup = useRef<HTMLDivElement>(null);
  const stageColumn = useRef<HTMLDivElement>(null);
  const composer = useRef<HTMLTextAreaElement>(null);

  const { view } = data;
  const current = view.current;
  const labelOf = (clip: ClipEntry) => {
    const row = view.rows.find((entry) => entry.clipId === clip.id);
    return row === undefined ? '' : t('web:clipPlayer.compare.label', { date: row.dateShort, kgReps: row.meta });
  };
  const select = (index: number | null) => {
    const row = index === null ? undefined : view.rows[index];
    if (row !== undefined && row.clipId !== clipId) {
      setStatus('');
      onSelectClip(row.clipId);
    }
  };
  const toggleTool = (tool: PlayerTool) => {
    setTools((current) => ({ ...current, [tool]: !current[tool] }));
  };

  const run = (command: PlayerCommand) => {
    switch (command.kind) {
      case 'togglePlay':
        playback.togglePlay();
        return;
      case 'seekBy':
        playback.seekBy(command.seconds);
        return;
      case 'frame':
        playback.frame(command.by);
        return;
      case 'rate':
        playback.stepRate(command.by);
        return;
      case 'seekFraction':
        playback.seekTo(command.fraction * playback.duration);
        return;
      case 'seekEdge':
        playback.seekTo(command.to === 'start' ? 0 : playback.duration);
        return;
      case 'volume':
        playback.volumeBy(command.by);
        return;
      case 'toggleMute':
        playback.toggleMute();
        return;
      case 'fullscreen':
        if (document.fullscreenElement === null) void stageColumn.current?.requestFullscreen().catch(() => undefined);
        else void document.exitFullscreen().catch(() => undefined);
        return;
      case 'clip':
        select(neighbourIndex(view.index, command.by, view.rows.length));
        return;
      case 'nextUnread':
        select(nextUnreadIndex(view.rows.map((row) => row.unread), view.index));
        return;
      case 'commentJump': {
        const target = pinJump(
          view.pins.map((pin) => pin.seconds),
          playback.currentTime(),
          command.by,
        );
        if (target !== null) playback.seekTo(target);
        return;
      }
      case 'compose':
        playback.pause();
        composer.current?.focus();
        return;
      case 'loopFrom':
        playback.loopFrom();
        return;
      case 'loopTo':
        playback.loopTo();
        return;
      case 'loopClear':
        playback.loopClear();
        return;
      case 'compare':
      case 'zoom':
      case 'theatre':
        toggleTool(command.kind);
        return;
      case 'pan':
        setOrigin((at) => panOrigin(at, command.dx, command.dy));
        return;
      case 'shortcuts':
        setSheet(true);
        return;
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target;
    // A menu or the shortcuts sheet is its own keyboard; this handler only hears it through the portal.
    if (target instanceof Element && target.closest('[role="menu"], [role="dialog"]') !== popup.current) return;
    if (popup.current !== null) wrapTab(event, popup.current);
    if (event.defaultPrevented) return;
    const command = playerCommand({
      key: event.key,
      shiftKey: event.shiftKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      altKey: event.altKey,
      target: keyTargetOf(target),
      zoomed: tools.zoom,
    });
    if (command === null) return;
    event.preventDefault();
    run(command);
  };

  return (
    <Dialog.Root
      onOpenChange={(open, details) => {
        if (open) return;
        if (details.reason === 'escape-key' && isTextEntry(details.event.target)) {
          details.cancel();
          return;
        }
        onClose();
      }}
      open
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup
          aria-label={t('web:clipPlayer.label')}
          className={styles.popup}
          initialFocus={popup}
          onKeyDown={onKeyDown}
          ref={popup}
        >
          <header className={styles.bar}>
            <Dialog.Close className={`${String(ghost.ghost)} ${String(styles.back)}`}>
              <ChevronGlyph className={styles.glyphSmall} direction="left" />
              {backLabel ?? t('web:clipPlayer.back')}
            </Dialog.Close>
            <AvatarChip avatarUrl={data.clientAvatarUrl} initials={data.clientInitials} variant="clip" />
            <div className={styles.titles}>
              <Dialog.Title className={styles.exercise}>{data.exerciseName}</Dialog.Title>
              <span className={styles.client}>{data.clientName}</span>
            </div>
            <span className={styles.spacer} />
            {view.index === -1 ? null : (
              <span className={styles.position}>
                {t('web:clipPlayer.position', { index: view.index + 1, total: view.rows.length })}
              </span>
            )}
            <button
              aria-label={t('web:clipPlayer.prev')}
              className={styles.icon}
              onClick={() => {
                run({ kind: 'clip', by: -1 });
              }}
              type="button"
            >
              <ChevronGlyph className={styles.glyph} direction="up" />
            </button>
            <button
              aria-label={t('web:clipPlayer.next')}
              className={styles.icon}
              onClick={() => {
                run({ kind: 'clip', by: 1 });
              }}
              type="button"
            >
              <ChevronGlyph className={styles.glyph} direction="down" />
            </button>
            <button
              className={`${String(ghost.ghost)} ${String(styles.ghost)}`}
              onClick={() => {
                setSheet(true);
              }}
              type="button"
            >
              <KeyboardGlyph className={styles.glyphSmall} />
              {t('web:clipPlayer.shortcuts')}
              <kbd className={`${String(kbd.kbd)} ${String(styles.kbdInk)}`}>?</kbd>
            </button>
            <Dialog.Close aria-label={t('web:clipPlayer.close')} className={styles.icon}>
              <CloseGlyph className={styles.glyph} />
            </Dialog.Close>
          </header>

          <div className={styles.body}>
            <PlayerList
              capacity={data.capacity}
              clipId={clipId}
              folded={tools.theatre}
              onSelect={onSelectClip}
              pending={data.pending}
              rows={view.rows}
            />

            <div className={styles.stageColumn} ref={stageColumn}>
              {current === null ? (
                <div className={styles.stageArea}>
                  <div className={styles.stageNotice} role="status">
                    {data.gone ? (
                      t('web:clipPlayer.states.gone')
                    ) : data.failed ? (
                      <>
                        {t('web:clipPlayer.states.failed')}
                        <button
                          className={`${String(ghost.ghost)} ${String(styles.small)}`}
                          onClick={data.retry}
                          type="button"
                        >
                          {t('web:clipPlayer.states.retry')}
                        </button>
                      </>
                    ) : (
                      <span className={hidden.srOnly}>{t('web:clipPlayer.states.loading')}</span>
                    )}
                  </div>
                </div>
              ) : (
                <PlayerStage
                  clip={current}
                  compareWith={tools.compare ? view.compareWith : null}
                  labelOf={labelOf}
                  onOrigin={setOrigin}
                  onReloadFilm={data.reloadFilm}
                  origin={origin}
                  playback={playback}
                  videoLabel={data.exerciseName}
                  zoomed={tools.zoom}
                />
              )}
              <PlayerControls
                onTool={toggleTool}
                pins={view.pins}
                playback={playback}
                tools={tools}
                videoUrl={current?.media.videoUrl ?? null}
              />
            </div>

            <PlayerAside
              clipId={clipId}
              composerRef={composer}
              data={data}
              folded={tools.theatre}
              onBackToFilm={() => {
                popup.current?.focus();
              }}
              onStatus={setStatus}
              playback={playback}
              onTyping={() => {
                playback.pause();
                setStatus('');
              }}
              status={status}
            />
          </div>

          <ShortcutsSheet finalFocus={popup} onOpenChange={setSheet} open={sheet} />
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
